import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { http } from "../../lib/api";

const COLS = [["draft", "Draft"], ["review1", "Customer review (1)"], ["revision", "Revision"], ["review2", "Customer review (2)"], ["finalise", "Finalise"], ["complete", "Complete"], ["declined", "Declined"]];
const PAY = { paid: ["Paid in full", "bg-emerald-100 text-emerald-800"], deposit_paid: ["Deposit paid", "bg-sky-100 text-sky-800"],
  deposit_due: ["Deposit due", "bg-amber-100 text-amber-800"], payment_due: ["Payment due", "bg-amber-100 text-amber-800"], no_quote: ["No price yet", "bg-stone-100 text-stone-600"] };
const daysIn = (r) => Math.max(0, Math.floor((Date.now() - new Date(r?.stage_at || r?.created_at || Date.now())) / 864e5));

const Card = ({ k, r }) => {
  const p = k.progress || {}, pct = p.total ? Math.round((100 * (p.done || 0)) / p.total) : 0, pc = k.page_check || {};
  const [pay, payCls] = PAY[k.bespoke?.pay] || PAY.no_quote, d = daysIn(r);
  return (
    <Link to={`/kits/${k.id}`} draggable={!!r} onDragStart={(e) => e.dataTransfer.setData("text/plain", r.id)}
      data-testid={`board-card-${k.id}`} className="block cursor-grab rounded-lg border border-stone-200 bg-white p-3 shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing">
      <div className="font-display text-lg leading-tight text-[#2B2A26]">{k.destination}</div>
      <div className="mb-2 text-xs text-stone-500">{k.bespoke.name}</div>
      <div className="mb-2 flex flex-wrap gap-1">
        <span data-testid={`board-card-pay-${k.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${payCls}`}>{pay}</span>
        <span data-testid={`board-card-days-${k.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${d >= 7 ? "bg-red-100 text-red-700" : "bg-stone-100 text-stone-600"}`}>{d} day{d === 1 ? "" : "s"} here</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-stone-100"><div className="h-full bg-[#9A7B2C]" style={{ width: `${pct}%` }} /></div>
      <div data-testid={`board-card-progress-${k.id}`} className="mt-1 text-[11px] text-stone-500">
        {pct}% built · {!pc.fresh ? "Page check not run" : pc.fails ? `Page check: ${pc.fails} issue${pc.fails === 1 ? "" : "s"}` : "Page check passed"}
      </div>
    </Link>
  );
};

export const BespokeBoard = ({ rows, setRows }) => {
  const [over, setOver] = useState(null);
  const drop = async (e, stage) => {
    e.preventDefault(); setOver(null);
    const rid = e.dataTransfer.getData("text/plain"), row = rows.find((x) => x.r?.id === rid);
    if (!row || row.r.stage === stage) return;
    const prev = rows, at = new Date().toISOString();
    setRows(rows.map((x) => (x.r?.id === rid ? { ...x, r: { ...x.r, stage, stage_at: at } } : x)));
    try { await http.patch(`/bespoke/requests/${rid}`, { stage }); toast.success(`Moved to ${COLS.find((c) => c[0] === stage)[1]}`); }
    catch { setRows(prev); toast.error("Couldn't move that kit — try again"); }
  };
  return (
    <div data-testid="bespoke-board" className="flex gap-4 overflow-x-auto pb-4">
      {COLS.map(([s, label]) => {
        const items = rows.filter((x) => (x.r?.stage || "draft") === s);
        return (
          <div key={s} data-testid={`board-col-${s}`} onDragOver={(e) => { e.preventDefault(); setOver(s); }} onDragLeave={() => setOver(null)} onDrop={(e) => drop(e, s)}
            className={`w-64 shrink-0 rounded-xl border p-3 transition-colors ${over === s ? "border-[#9A7B2C] bg-amber-50" : "border-stone-200 bg-stone-50"}`}>
            <div className="mb-3 flex items-center justify-between text-xs font-bold uppercase tracking-wide text-stone-600">
              {label}<span data-testid={`board-col-count-${s}`} className="rounded-full bg-white px-2 py-0.5 text-stone-500">{items.length}</span>
            </div>
            <div className="min-h-[80px] space-y-2">{items.map(({ k, r }) => <Card key={k.id} k={k} r={r} />)}</div>
          </div>
        );
      })}
    </div>
  );
};
